/**
 * Milkath Landing V1 — world-class marketing home.
 *
 * Design notes
 * ────────────
 *  • Dark theme by default (#0B0B0B).
 *  • Brand accent orange (#FF7A00). Text on dark: white / zinc-300 / zinc-500.
 *  • Framer Motion for entrance + scroll reveals.
 *  • Glass-morphism cards (backdrop-blur + subtle border), soft radial gradients.
 *  • Uploaded logo is used verbatim — never regenerated / recolored.
 *  • Every interactive element carries a data-testid for future automation.
 *
 * Sections (numbered in JSX):
 *   1. Sticky navbar
 *   2. Hero + dashboard mockup
 *   3. Trusted platforms strip
 *   4. Features grid
 *   5. How it works (4 steps)
 *   6. Why Milkath — stat cards
 *   7. Final CTA
 *   8. Footer
 */
import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import {
  ArrowRight, Play, ShoppingBag, Sparkles, Users, Package, Store, Layers,
  Upload, Wand2, Brain, TrendingUp, Check, Menu, X, Zap, Award, Rocket,
  ArrowUpRight, ArrowDownRight,
} from "lucide-react";
import { Logo } from "../components/app/Logo";


// Framer variants — used everywhere for consistent motion feel.
const fadeUp = {
  hidden: { opacity: 0, y: 22 },
  show:   { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] } },
};
const stagger = { show: { transition: { staggerChildren: 0.08 } } };


// ────────────────────────────────────────────────────────────────
// 1 · STICKY NAVBAR
// ────────────────────────────────────────────────────────────────
function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", on, { passive: true });
    return () => window.removeEventListener("scroll", on);
  }, []);
  const links = [
    { label: "Features",    href: "#features"    },
    { label: "How it Works", href: "#how"         },
    { label: "Contact",      href: "#contact"     },
  ];
  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-40 transition-all duration-300 ${
          scrolled
            ? "bg-[#0B0B0B]/80 backdrop-blur-xl border-b border-white/5"
            : "bg-transparent"
        }`}
      >
        <nav className="max-w-7xl mx-auto flex items-center justify-between px-6 lg:px-10 h-16 sm:h-18">
          <Link to="/" data-testid="nav-home"><Logo size={30} /></Link>

          <div className="hidden md:flex items-center gap-1">
            {links.map((l) => (
              <a
                key={l.href}
                href={l.href}
                data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g,"-")}`}
                className="text-sm text-zinc-400 hover:text-white px-4 py-2 rounded-lg transition-colors"
              >
                {l.label}
              </a>
            ))}
          </div>

          <div className="hidden md:flex items-center gap-3">
            <Link
              to="/login"
              data-testid="nav-login"
              className="text-sm text-zinc-300 hover:text-white px-4 py-2 rounded-lg transition-colors"
            >
              Login
            </Link>
            <Link
              to="/signup"
              data-testid="nav-cta"
              className="text-sm font-medium bg-[#FF7A00] hover:bg-[#ff8b1a] text-white px-4 py-2 rounded-full transition-colors shadow-[0_6px_24px_-8px_rgba(255,122,0,0.7)]"
            >
              Start Free
            </Link>
          </div>

          <button
            className="md:hidden p-2 rounded-lg text-zinc-300 hover:bg-white/5"
            onClick={() => setOpen(true)}
            data-testid="nav-mobile-open"
            aria-label="Open menu"
          >
            <Menu className="h-5 w-5" />
          </button>
        </nav>
      </header>

      {/* Mobile drawer */}
      {open && (
        <div className="fixed inset-0 z-50 md:hidden" data-testid="mobile-menu">
          <div className="absolute inset-0 bg-black/80" onClick={() => setOpen(false)} />
          <div className="absolute inset-y-0 right-0 w-80 max-w-full bg-[#0B0B0B] border-l border-white/5 p-6 flex flex-col">
            <div className="flex items-center justify-between mb-8">
              <Logo size={28} />
              <button onClick={() => setOpen(false)} className="p-2 rounded-lg text-zinc-400 hover:text-white" aria-label="Close menu">
                <X className="h-5 w-5" />
              </button>
            </div>
            <nav className="flex flex-col gap-1">
              {links.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={() => setOpen(false)}
                  className="text-base text-zinc-300 hover:text-white py-3 border-b border-white/5"
                >
                  {l.label}
                </a>
              ))}
            </nav>
            <div className="mt-auto flex flex-col gap-2 pt-6">
              <Link to="/login" className="text-center text-sm py-2.5 border border-white/10 rounded-full text-zinc-200 hover:bg-white/5">Login</Link>
              <Link to="/signup" className="text-center text-sm py-2.5 bg-[#FF7A00] rounded-full text-white font-medium">Start Free</Link>
            </div>
          </div>
        </div>
      )}
    </>
  );
}


// ────────────────────────────────────────────────────────────────
// 2 · HERO
// ────────────────────────────────────────────────────────────────
function DashboardMockup() {
  // A hand-drawn mockup that hints at Milkath's real dashboard without
  // copying a screenshot. Uses brand colors + subtle motion.
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.96, y: 20 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      transition={{ duration: 1, ease: [0.16, 1, 0.3, 1], delay: 0.15 }}
      className="relative"
      data-testid="hero-mockup"
    >
      {/* Ambient glow behind the card */}
      <div className="absolute -inset-20 bg-[radial-gradient(ellipse_at_center,rgba(255,122,0,0.18),transparent_60%)] blur-2xl pointer-events-none" />

      <div className="relative rounded-2xl border border-white/10 bg-gradient-to-b from-zinc-900/90 to-zinc-950/90 backdrop-blur-xl shadow-[0_30px_100px_-20px_rgba(0,0,0,0.9)] overflow-hidden">
        {/* window chrome */}
        <div className="flex items-center gap-2 px-4 py-3 border-b border-white/5">
          <span className="h-2.5 w-2.5 rounded-full bg-red-500/70" />
          <span className="h-2.5 w-2.5 rounded-full bg-yellow-500/70" />
          <span className="h-2.5 w-2.5 rounded-full bg-emerald-500/70" />
          <span className="ml-3 text-[10px] uppercase tracking-[0.18em] text-zinc-500">milkath.com/dashboard</span>
        </div>

        <div className="p-5 sm:p-7 space-y-5">
          {/* Business Health hero card */}
          <div className="rounded-xl border border-[#FF7A00]/25 bg-gradient-to-r from-[#FF7A00]/10 via-[#FF7A00]/5 to-transparent p-4 sm:p-5">
            <p className="text-[10px] uppercase tracking-[0.18em] text-[#FF7A00] font-bold">Today · Business Health</p>
            <div className="mt-2 flex items-baseline gap-3">
              <span className="text-4xl sm:text-5xl font-semibold text-white tracking-tight" style={{ fontFamily: "var(--font-heading)" }}>92</span>
              <span className="text-zinc-500 text-xs">/100 · Excellent</span>
            </div>
            <p className="mt-1 text-sm text-zinc-300">Revenue growth · Product diversity · Marketplace mix all trending up.</p>
          </div>

          {/* KPI row */}
          <div className="grid grid-cols-3 gap-3">
            {[
              { label: "Revenue",  value: "₹24,890",  delta: "+18.4%", up: true },
              { label: "Orders",   value: "10",        delta: "+7.2%",  up: true },
              { label: "AOV",      value: "₹2,489",    delta: "-2.1%",  up: false },
            ].map((k, i) => (
              <motion.div
                key={k.label}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 + i * 0.08, duration: 0.5 }}
                className="rounded-lg bg-white/[0.03] border border-white/5 p-3"
              >
                <p className="text-[9px] uppercase tracking-[0.18em] text-zinc-500 font-bold">{k.label}</p>
                <p className="mt-1 text-lg font-semibold text-white tabular-nums" style={{ fontFamily: "var(--font-heading)" }}>{k.value}</p>
                <p className={`mt-0.5 text-[10px] tabular-nums flex items-center gap-0.5 ${k.up ? "text-emerald-400" : "text-red-400"}`}>
                  {k.up ? <ArrowUpRight className="h-3 w-3" /> : <ArrowDownRight className="h-3 w-3" />}{k.delta}
                </p>
              </motion.div>
            ))}
          </div>

          {/* Sparkline */}
          <div className="rounded-lg bg-white/[0.02] border border-white/5 p-4 h-32 relative overflow-hidden">
            <p className="text-[9px] uppercase tracking-[0.18em] text-zinc-500 font-bold mb-2">Revenue · last 30 days</p>
            <svg viewBox="0 0 300 60" className="w-full h-16" preserveAspectRatio="none">
              <defs>
                <linearGradient id="area" x1="0" x2="0" y1="0" y2="1">
                  <stop offset="0" stopColor="#FF7A00" stopOpacity="0.35" />
                  <stop offset="1" stopColor="#FF7A00" stopOpacity="0" />
                </linearGradient>
              </defs>
              <path d="M0 45 L20 40 L40 42 L60 35 L80 38 L100 30 L120 25 L140 28 L160 22 L180 24 L200 18 L220 20 L240 12 L260 15 L280 8 L300 5 L300 60 L0 60 Z" fill="url(#area)" />
              <path d="M0 45 L20 40 L40 42 L60 35 L80 38 L100 30 L120 25 L140 28 L160 22 L180 24 L200 18 L220 20 L240 12 L260 15 L280 8 L300 5" stroke="#FF7A00" strokeWidth="1.6" fill="none" />
            </svg>
          </div>

          {/* Opportunity teaser */}
          <div className="rounded-lg border border-white/5 bg-white/[0.03] p-4 flex items-start gap-3">
            <div className="h-8 w-8 rounded-lg bg-[#FF7A00]/15 border border-[#FF7A00]/30 flex items-center justify-center shrink-0">
              <Sparkles className="h-4 w-4 text-[#FF7A00]" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-bold uppercase tracking-[0.14em] text-emerald-400">Opportunity · 78% confidence</p>
              <p className="mt-1 text-sm text-white">
                <span className="font-medium">Running Shoes Alpha</span> gaining momentum — up 42% w/w.
              </p>
              <p className="mt-0.5 text-xs text-zinc-400">Suggested action: double ad-spend on Flipkart next 7 days.</p>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

function Hero() {
  return (
    <section className="relative pt-28 sm:pt-32 pb-20 sm:pb-28 overflow-hidden" data-testid="hero-section">
      {/* Ambient orange halo */}
      <div className="absolute top-0 inset-x-0 -z-10 h-[600px] bg-[radial-gradient(ellipse_60%_50%_at_50%_0%,rgba(255,122,0,0.15),transparent_60%)]" />
      <div className="absolute inset-0 -z-10 bg-[linear-gradient(180deg,#0B0B0B_0%,#0B0B0B_100%)]" />

      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
        <motion.div
          initial="hidden" animate="show" variants={stagger}
          className="max-w-xl"
        >
          <motion.div variants={fadeUp} className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.03] backdrop-blur px-3 py-1.5 text-xs text-zinc-300 mb-6">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />
            AI-powered · Built for Indian e-commerce
          </motion.div>

          <motion.h1
            variants={fadeUp}
            className="text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-white leading-[1.05]"
            style={{ fontFamily: "var(--font-heading)" }}
            data-testid="hero-headline"
          >
            Stop Guessing.<br />
            <span className="text-[#FF7A00]">Start Growing Revenue.</span>
          </motion.h1>

          <motion.p variants={fadeUp} className="mt-6 text-base sm:text-lg text-zinc-400 leading-relaxed max-w-xl" data-testid="hero-subhead">
            Upload your Amazon, Flipkart, Shopify, WooCommerce or Meesho order history. Milkath&rsquo;s AI finds hidden revenue opportunities, customer insights and growth recommendations in under 60 seconds.
          </motion.p>

          <motion.div variants={fadeUp} className="mt-8 flex flex-col sm:flex-row gap-3">
            <Link
              to="/signup"
              data-testid="hero-cta-primary"
              className="group inline-flex items-center justify-center gap-2 bg-[#FF7A00] hover:bg-[#ff8b1a] text-white text-sm font-medium px-6 py-3 rounded-full transition-all shadow-[0_10px_40px_-10px_rgba(255,122,0,0.7)] hover:shadow-[0_14px_50px_-10px_rgba(255,122,0,0.85)]"
            >
              Start Free Revenue Audit
              <ArrowRight className="h-4 w-4 group-hover:translate-x-0.5 transition-transform" />
            </Link>
            <a
              href="#how"
              data-testid="hero-cta-secondary"
              className="inline-flex items-center justify-center gap-2 border border-white/10 bg-white/[0.03] hover:bg-white/[0.06] text-white text-sm font-medium px-6 py-3 rounded-full transition-colors"
            >
              <Play className="h-4 w-4" /> Watch Demo
            </a>
          </motion.div>

          <motion.p variants={fadeUp} className="mt-6 text-xs text-zinc-500">
            No credit card. Setup takes about a minute.
          </motion.p>
        </motion.div>

        <DashboardMockup />
      </div>
    </section>
  );
}


// ────────────────────────────────────────────────────────────────
// 3 · TRUSTED PLATFORMS
// ────────────────────────────────────────────────────────────────
const PLATFORMS = [
  { name: "Amazon",       tint: "from-orange-500/20 to-yellow-500/10", icon: ShoppingBag },
  { name: "Flipkart",     tint: "from-yellow-400/20 to-blue-500/10",   icon: Store },
  { name: "Shopify",      tint: "from-emerald-500/20 to-teal-500/10",  icon: Layers },
  { name: "WooCommerce",  tint: "from-fuchsia-500/20 to-purple-500/10", icon: Package },
  { name: "Meesho",       tint: "from-pink-500/20 to-rose-500/10",     icon: Users },
];

function Platforms() {
  return (
    <section className="py-20 sm:py-24 border-t border-white/5" data-testid="platforms-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <motion.div
          initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.3 }} variants={stagger}
          className="text-center max-w-2xl mx-auto mb-14"
        >
          <motion.p variants={fadeUp} className="text-xs uppercase tracking-[0.2em] text-[#FF7A00] font-bold">Integrations</motion.p>
          <motion.h2 variants={fadeUp} className="mt-3 text-3xl sm:text-4xl font-semibold tracking-tight text-white" style={{ fontFamily: "var(--font-heading)" }}>
            Works With Your Commerce Stack
          </motion.h2>
          <motion.p variants={fadeUp} className="mt-3 text-zinc-400">
            Zero-mapping imports for every major Indian e-commerce platform.
          </motion.p>
        </motion.div>

        <motion.div
          initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.2 }} variants={stagger}
          className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4"
        >
          {PLATFORMS.map((p) => (
            <motion.div
              key={p.name}
              variants={fadeUp}
              whileHover={{ y: -4 }}
              transition={{ type: "spring", stiffness: 260, damping: 20 }}
              className="group relative overflow-hidden rounded-2xl border border-white/10 bg-white/[0.02] backdrop-blur p-6"
              data-testid={`platform-${p.name.toLowerCase()}`}
            >
              <div className={`absolute inset-0 bg-gradient-to-br ${p.tint} opacity-0 group-hover:opacity-100 transition-opacity duration-500`} />
              <div className="relative">
                <div className="h-10 w-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center mb-4">
                  <p.icon className="h-5 w-5 text-white" strokeWidth={1.5} />
                </div>
                <p className="text-white font-medium">{p.name}</p>
                <p className="mt-1 text-xs text-zinc-500">Auto-detected</p>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}


// ────────────────────────────────────────────────────────────────
// 4 · FEATURES
// ────────────────────────────────────────────────────────────────
const FEATURES = [
  {
    icon: Upload,   title: "Universal Commerce Import Engine",
    body: "Automatically recognizes marketplace CSVs with zero manual mapping — Amazon, Flipkart, Shopify, WooCommerce, Meesho and more.",
  },
  {
    icon: Sparkles, title: "AI Revenue Intelligence",
    body: "Discover hidden revenue opportunities instantly — every insight tied to a specific action with estimated impact.",
  },
  {
    icon: Users,    title: "Customer Intelligence",
    body: "Identify repeat buyers, inactive customers and retention opportunities with LTV cohorts.",
  },
  {
    icon: Package,  title: "Product Intelligence",
    body: "Know your winners, slow movers and underperforming products across every SKU.",
  },
  {
    icon: Store,    title: "Marketplace Intelligence",
    body: "Compare revenue, growth and margins across every sales channel side-by-side.",
  },
  {
    icon: Brain,    title: "Executive Dashboard",
    body: "All business metrics in one intelligent workspace — grounded in the single BI source of truth.",
  },
];

function Features() {
  return (
    <section id="features" className="py-20 sm:py-28 border-t border-white/5" data-testid="features-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <motion.div initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.3 }} variants={stagger} className="max-w-2xl mb-14">
          <motion.p variants={fadeUp} className="text-xs uppercase tracking-[0.2em] text-[#FF7A00] font-bold">Features</motion.p>
          <motion.h2 variants={fadeUp} className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-white" style={{ fontFamily: "var(--font-heading)" }}>
            Six modules. One live business brain.
          </motion.h2>
          <motion.p variants={fadeUp} className="mt-4 text-zinc-400 text-lg">
            Each module reads the same trusted BI engine — so every number you see is one you can act on.
          </motion.p>
        </motion.div>

        <motion.div initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.15 }} variants={stagger} className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {FEATURES.map((f, i) => (
            <motion.div
              key={f.title}
              variants={fadeUp}
              whileHover={{ y: -6 }}
              transition={{ type: "spring", stiffness: 220, damping: 22 }}
              className="group relative overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-br from-white/[0.04] to-white/[0.01] backdrop-blur p-6 sm:p-7"
              data-testid={`feature-${i}`}
            >
              <div className="absolute -top-16 -right-16 h-40 w-40 rounded-full bg-[#FF7A00]/20 blur-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              <div className="relative">
                <div className="h-11 w-11 rounded-xl bg-[#FF7A00]/10 border border-[#FF7A00]/30 flex items-center justify-center mb-5">
                  <f.icon className="h-5 w-5 text-[#FF7A00]" strokeWidth={1.6} />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2" style={{ fontFamily: "var(--font-heading)" }}>{f.title}</h3>
                <p className="text-sm text-zinc-400 leading-relaxed">{f.body}</p>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}


// ────────────────────────────────────────────────────────────────
// 5 · HOW IT WORKS
// ────────────────────────────────────────────────────────────────
const STEPS = [
  { icon: Upload,     n: "01", title: "Upload CSV",
    body: "Drag & drop your order-history file from any supported marketplace." },
  { icon: Wand2,      n: "02", title: "Auto-mapping",
    body: "Milkath understands your data automatically — no column-mapping ever." },
  { icon: Brain,      n: "03", title: "AI analysis",
    body: "The BI engine crunches revenue, customers, products, marketplaces." },
  { icon: TrendingUp, n: "04", title: "Prioritized actions",
    body: "Receive prioritized revenue opportunities ranked by estimated impact." },
];

function HowItWorks() {
  return (
    <section id="how" className="py-20 sm:py-28 border-t border-white/5" data-testid="how-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <motion.div initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.3 }} variants={stagger} className="text-center max-w-2xl mx-auto mb-16">
          <motion.p variants={fadeUp} className="text-xs uppercase tracking-[0.2em] text-[#FF7A00] font-bold">How it works</motion.p>
          <motion.h2 variants={fadeUp} className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-white" style={{ fontFamily: "var(--font-heading)" }}>
            First insight in under a minute.
          </motion.h2>
        </motion.div>

        <motion.div initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.15 }} variants={stagger} className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 relative">
          {/* Connecting line for desktop */}
          <div className="hidden lg:block absolute top-14 left-[12.5%] right-[12.5%] h-px bg-gradient-to-r from-transparent via-[#FF7A00]/40 to-transparent" />
          {STEPS.map((s, i) => (
            <motion.div key={s.n} variants={fadeUp} className="relative" data-testid={`step-${i}`}>
              <div className="rounded-2xl border border-white/10 bg-white/[0.02] backdrop-blur p-6 sm:p-7 h-full">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-xs font-bold tracking-[0.18em] text-zinc-500">{s.n}</span>
                  <div className="h-10 w-10 rounded-xl bg-[#FF7A00]/10 border border-[#FF7A00]/30 flex items-center justify-center">
                    <s.icon className="h-4 w-4 text-[#FF7A00]" strokeWidth={1.7} />
                  </div>
                </div>
                <h3 className="text-lg font-semibold text-white mb-2" style={{ fontFamily: "var(--font-heading)" }}>{s.title}</h3>
                <p className="text-sm text-zinc-400 leading-relaxed">{s.body}</p>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}


// ────────────────────────────────────────────────────────────────
// 6 · WHY MILKATH — stats
// ────────────────────────────────────────────────────────────────
const STATS = [
  { icon: Zap,     title: "60 Second Setup",       body: "First insight served before your coffee cools." },
  { icon: Layers,  title: "Multi-Marketplace",     body: "Amazon, Flipkart, Shopify, WooCommerce, Meesho — all mixed into one truth." },
  { icon: Brain,   title: "AI Powered Insights",   body: "Grounded in a single BI engine — never hallucinated, never approximated." },
  { icon: Check,   title: "No Manual CSV Mapping", body: "Column names, delimiters and formats don't matter — we detect them." },
];

function WhyMilkath() {
  return (
    <section className="py-20 sm:py-28 border-t border-white/5" data-testid="why-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-12 items-start">
        <motion.div initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.3 }} variants={stagger}>
          <motion.p variants={fadeUp} className="text-xs uppercase tracking-[0.2em] text-[#FF7A00] font-bold">Why Milkath</motion.p>
          <motion.h2 variants={fadeUp} className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-white leading-tight" style={{ fontFamily: "var(--font-heading)" }}>
            Don&rsquo;t spend hours <br /> inside spreadsheets.
          </motion.h2>
          <motion.p variants={fadeUp} className="mt-5 text-zinc-400 leading-relaxed text-lg max-w-md">
            Milkath transforms raw order history into actionable business intelligence that helps e-commerce brands grow revenue faster.
          </motion.p>
        </motion.div>

        <motion.div initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.15 }} variants={stagger} className="grid sm:grid-cols-2 gap-3">
          {STATS.map((s, i) => (
            <motion.div key={s.title} variants={fadeUp} className="rounded-2xl border border-white/10 bg-white/[0.02] backdrop-blur p-5" data-testid={`stat-${i}`}>
              <div className="h-9 w-9 rounded-lg bg-[#FF7A00]/10 border border-[#FF7A00]/30 flex items-center justify-center mb-3">
                <s.icon className="h-4 w-4 text-[#FF7A00]" strokeWidth={1.7} />
              </div>
              <p className="font-semibold text-white">{s.title}</p>
              <p className="mt-1 text-sm text-zinc-400 leading-relaxed">{s.body}</p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}


// ────────────────────────────────────────────────────────────────
// 7 · FINAL CTA
// ────────────────────────────────────────────────────────────────
function FinalCTA() {
  return (
    <section className="py-24 sm:py-32 border-t border-white/5 relative overflow-hidden" data-testid="final-cta-section">
      <div className="absolute inset-0 -z-10 bg-[radial-gradient(ellipse_at_center,rgba(255,122,0,0.16),transparent_60%)]" />
      <motion.div
        initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.4 }} variants={stagger}
        className="max-w-3xl mx-auto text-center px-6"
      >
        <motion.h2 variants={fadeUp} className="text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-white leading-[1.05]" style={{ fontFamily: "var(--font-heading)" }}>
          Your next revenue opportunity <br className="hidden sm:block" />
          is already in your data.
        </motion.h2>
        <motion.p variants={fadeUp} className="mt-6 text-zinc-400 text-lg max-w-xl mx-auto">
          Upload your first CSV and discover what your business is missing.
        </motion.p>
        <motion.div variants={fadeUp} className="mt-10">
          <Link
            to="/signup"
            data-testid="final-cta-btn"
            className="group inline-flex items-center gap-2 bg-[#FF7A00] hover:bg-[#ff8b1a] text-white text-base font-medium px-7 py-3.5 rounded-full transition-all shadow-[0_10px_50px_-10px_rgba(255,122,0,0.8)] hover:shadow-[0_18px_70px_-10px_rgba(255,122,0,0.95)]"
          >
            Start Free Revenue Audit
            <ArrowRight className="h-4 w-4 group-hover:translate-x-0.5 transition-transform" />
          </Link>
        </motion.div>
        <motion.p variants={fadeUp} className="mt-4 text-xs text-zinc-500">
          Free forever · No credit card · Data stays yours
        </motion.p>
      </motion.div>
    </section>
  );
}


// ────────────────────────────────────────────────────────────────
// 8 · FOOTER
// ────────────────────────────────────────────────────────────────
function Footer() {
  const cols = [
    { title: "Product",   links: [
      { label: "Features",  href: "#features"    },
      { label: "How it Works", href: "#how"      },
    ]},
    { title: "Legal",     links: [
      { label: "Privacy",   href: "/privacy"     },
      { label: "Terms",     href: "/terms"       },
    ]},
    { title: "Company",   links: [
      { label: "Contact",   href: "#contact"     },
    ]},
  ];
  return (
    <footer id="contact" className="border-t border-white/5 py-14" data-testid="footer">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid md:grid-cols-4 gap-10">
          <div>
            <Logo size={32} />
            <p className="mt-5 text-sm text-zinc-400 leading-relaxed max-w-xs">
              AI revenue intelligence for Indian e-commerce brands.
            </p>
          </div>
          {cols.map((c) => (
            <div key={c.title}>
              <h3 className="text-xs uppercase tracking-[0.2em] text-zinc-500 font-bold mb-4">{c.title}</h3>
              <ul className="space-y-2.5">
                {c.links.map((l) => (
                  <li key={l.label}>
                    <a href={l.href} className="text-sm text-zinc-400 hover:text-white transition-colors" data-testid={`footer-${l.label.toLowerCase().replace(/\s+/g,'-')}`}>{l.label}</a>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <div className="mt-12 pt-6 border-t border-white/5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <p className="text-xs text-zinc-500">© 2026 Milkath. All rights reserved.</p>
          <p className="text-xs text-zinc-600">Made in India · Built for Bharat.</p>
        </div>
      </div>
    </footer>
  );
}


// ────────────────────────────────────────────────────────────────
// PAGE
// ────────────────────────────────────────────────────────────────
export default function Landing() {
  return (
    <div className="min-h-screen bg-[#0B0B0B] text-white antialiased" data-testid="landing">
      <Navbar />
      <main>
        <Hero />
        <Platforms />
        <Features />
        <HowItWorks />
        <WhyMilkath />
        <FinalCTA />
      </main>
      <Footer />
    </div>
  );
}
